<template>
	<div
		class="root"
		:style="{
			'--background-mobile': getBackgroundVar(backgroundMobile),
			'--background-desktop': getBackgroundVar(backgroundDesktop),
			'--header-height-mobile': increasedHeightMobile
				? '260px'
				: 'var(--header-height)',
		}"
	>
		<div class="self">
			<div
				:class="{
					container: width === 'container',
					'container--fluid': width === 'full',
				}"
			>
				<div class="selfContent">
					<router-link class="logo" :to="{ name: 'Dashboard' }">
						<img src="@/branding/img/logo.svg" alt="" />
					</router-link>

					<div class="mainDesktop">
						<div class="desktopLinks">
							<template v-for="link in links.main">
								<router-link
									v-if="link.requiresAuth && user"
									:key="link.to"
									:to="link.to"
									class="desktopLink link"
									>{{ $t(link.label) }}</router-link
								>
							</template>
						</div>

						<div class="accountItems">
							<BDropdown class="accountDropdown" v-if="user">
								<template #toggle>
									<div class="accountDropdownToggle"></div>
								</template>

								<BDropdownItem v-for="link in links.account" :key="link.to">
									<router-link class="block" :to="link.to">{{
										$t(link.label)
									}}</router-link></BDropdownItem
								>
							</BDropdown>

							<BLangSelector v-model="selectedLang" :options="langOptions" />
						</div>
					</div>

					<div class="mainMobile">
						<button class="mobileMenuToggle" @click="toggleMobileMenu">
							<BIcon :name="isMobileMenuOpen ? 'close' : 'Menu'" />
						</button>
					</div>
				</div>
			</div>
		</div>

		<div class="mobileMenu" v-if="isMobileMenuOpen">
			<div class="mobileMenu__accountItems">
				<div class="mobileMenu__accountItemsContainer container">
					<div class="flex align-items-center" :style="{ gap: '10px' }">
						<div class="userAvatar"></div>
						<div class="color-neutral-600">
							{{ user && `${user.firstName} ${user.lastName}` }}
						</div>
					</div>

					<BSelect v-model="selectedLang" :options="langOptions"></BSelect>
				</div>
			</div>

			<div class="mobileMenu__links">
				<div class="mobileMenu__linksContainer container">
					<template v-for="link in [...links.main, ...links.account]">
						<router-link
							v-if="link.requiresAuth && user"
							:key="link.to"
							:to="link.to"
							class="mobileMenuLink link"
							@click.native="handleMobileLinkClick"
							>{{ $t(link.label) }}</router-link
						>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { BDropdown, BDropdownItem } from "@/components/base/BDropdown/index.js";
import BLangSelector from "@/components/base/BLangSelector";
import BSelect from "@/components/base/BSelect";
import BIcon from "@/components/base/BIcon/index.vue";
import { mapState } from "vuex";
import Vue from "vue";

export default {
	components: {
		BDropdown,
		BDropdownItem,
		BLangSelector,
		BSelect,
		BIcon,
	},
	props: {
		backgroundMobile: {
			type: String,
			default: "var(--color-neutral-50)",
		},
		backgroundDesktop: {
			type: String,
			default: "var(--color-neutral-50)",
		},
		increasedHeightMobile: {
			type: Boolean,
			default: false,
		},
		width: {
			type: String,
			default: "container",
		},
	},
	data() {
		return {
			links: {
				main: [
					{ label: "Nova Simulação", to: "/simulator", requiresAuth: true },
				],
				account: [
					{ label: "Simulações", to: "/simulations", requiresAuth: true },
					{ label: "Logout", to: "/logout", requiresAuth: true },
				],
			},
			isMobileMenuOpen: false,
			boundResizeListener: null,
		};
	},
	computed: {
		...mapState("user", ["user"]),
		langOptions() {
			return this.$t.availableLangs.map((lang) => ({
				id: lang,
				label: lang.toUpperCase(),
			}));
		},
		selectedLang: {
			get() {
				return this.$store.state.user.lang;
			},
			set(value) {
				if (this.user) {
					//updated account language
				}

				this.$store.dispatch("user/setLang", value);
			},
		},
	},
	methods: {
		getBackgroundVar(background) {
			const imgExtensions = ["jpg", "jpeg", "png", "gif", "svg"];
			if (imgExtensions.some((ext) => background.endsWith(`.${ext}`))) {
				return `url(${require(`../../assets/img/${background}`)})`;
			} else {
				return background;
			}
		},
		handleChange(event) {
			if (this.disabled) {
				return;
			}

			const newValue = event.target.checked
				? [...this.value, this.optionValue]
				: this.value.filter((item) => item !== this.optionValue);

			this.$emit("input", newValue);
		},

		toggleMobileMenu() {
			if (this.isMobileMenuOpen) {
				this.closeMobileMenu();
			} else {
				this.openMobileMenu();
			}
		},

		closeMobileMenu() {
			this.isMobileMenuOpen = false;
			document.body.style.overflow = "";
		},

		openMobileMenu() {
			this.isMobileMenuOpen = true;
			window.scrollTo(0, 0);
			document.body.style.overflow = "hidden";
		},

		resizeListener() {
			if (window.innerWidth > 768) {
				this.closeMobileMenu();
			}
		},

		handleMobileLinkClick() {
			this.closeMobileMenu();
		},
	},

	watch: {
		$route() {
			//ideally this should be enough.
			//but when navigation is duplicated, the route change is not detected
			//so we need to close the menu manually
			this.closeMobileMenu();
		},
	},

	created() {
		this.boundsResizeListener = this.resizeListener.bind(this);
		window.addEventListener("resize", this.boundsResizeListener);
	},

	beforeDestroy() {
		window.removeEventListener("resize", this.boundsResizeListener);
	},
};
</script>

<style lang="scss" scoped>
.self {
	background: var(--background-desktop);
	background-size: cover;
}

.selfContent {
	display: flex;
	justify-content: space-between;
	align-items: center;
	height: var(--header-height);
	position: relative;
	z-index: 101;
}

@media (max-width: 768px) {
	.self {
		background: var(--background-mobile);
		background-size: cover;
		background-position: center;
		height: var(--header-height-mobile);
	}
	.selfContent {
	}
}

.logo {
	max-width: 250px;
}

.accountDropdownToggle,
.mobileMenuToggle,
.userAvatar {
	--size: 45px;
	width: var(--size);
	height: var(--size);
	border-radius: 50%;
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	display: flex;
	align-items: center;
	justify-content: center;
}

.userAvatar,
.accountDropdownToggle {
	background-color: var(--color-primary-800);
	background-image: url("../../assets/img/user_avatar.png");
}

.mobileMenuToggle {
	background-color: var(--color-neutral-0);
}

.mainDesktop {
	margin-left: auto;
	display: flex;
	gap: 40px;
	align-items: center;
}

.desktopLinks {
	display: flex;
	gap: 40px;
}

.link {
	color: var(--color-primary-700);
	cursor: pointer;

	&:hover {
		color: var(--color-primary-800);
	}

	&:active {
		color: var(--color-primary-900);
	}
}

.desktopLink {
	font-weight: var(--font-weight-medium);
}

.accountItems {
	display: flex;
	gap: 14px;
	align-items: center;
}

.mainMobile {
	display: none;
}

.mobileMenu {
	position: fixed;
	height: 100dvh;
	inset: 0;
	background: var(--color-neutral-50);
	z-index: 100;
	padding-top: var(--header-height);
	display: flex;
	flex-direction: column;
}

.mobileMenuContainer {
	display: flex;
	flex-direction: column;
}

.mobileMenu__accountItems {
	padding-bottom: 20px;
}

.mobileMenu__accountItemsContainer {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.mobileMenu__links {
	flex-grow: 1;
	background-color: #fff;
}

.mobileMenu__linksContainer {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 20px;
	padding-block: 60px;
}

.mobileMenuLink {
	font-size: var(--font-size-xl);
	font-weight: var(--font-weight-light);
}

@media (max-width: 768px) {
	.mainDesktop {
		display: none;
	}
	.mainMobile {
		display: flex;
		gap: 20px;
	}

	.logo {
		max-width: 185px;
	}
}
</style>
